import { useMutation, useQuery } from "@apollo/client/react";
import {
  CreateSharedItemCommentDocument,
  DeleteSharedItemCommentDocument,
  SharedItemCommentsDocument,
  UpdateSharedItemCommentDocument,
  type SharedItemCommentsQuery,
} from "@/graphql/generated/operations";

export type SharedItemComment = NonNullable<
  NonNullable<SharedItemCommentsQuery["sharedItemComments"]>["nodes"]
>[number];

export interface UseSharedItemCommentsResult {
  comments: readonly SharedItemComment[];
  totalCount: number;
  loading: boolean;
  error: Error | null;
  busy: boolean;
  add: (content: string) => Promise<void>;
  edit: (commentId: string, content: string) => Promise<void>;
  remove: (commentId: string) => Promise<void>;
  refetch: () => Promise<unknown>;
}

export function useSharedItemComments(itemId: string): UseSharedItemCommentsResult {
  const query = useQuery(SharedItemCommentsDocument, {
    // Newest first, capped at 50 (no "load more" UI yet). Cast around
    // codegen's nominal SortOrder split.
    variables: {
      input: {
        first: 50,
        where: { sharedItemId: { eq: itemId } },
        order: { postedAt: "DESC" },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    skip: !itemId,
  });

  const [createMut, createState] = useMutation(CreateSharedItemCommentDocument);
  const [updateMut, updateState] = useMutation(UpdateSharedItemCommentDocument);
  const [deleteMut, deleteState] = useMutation(DeleteSharedItemCommentDocument);

  const comments = query.data?.sharedItemComments?.nodes ?? [];
  const totalCount = query.data?.sharedItemComments?.totalCount ?? comments.length;

  async function add(content: string) {
    const trimmed = content.trim();
    if (!trimmed) return;
    // genesis liest nur data.sharedItem (Relation-Input); das Schwesterfeld
    // sharedItemId existiert zwar im Schema, fuehrt serverseitig aber zu einer NPE.
    await createMut({
      variables: { input: { data: { sharedItem: { id: itemId }, content: trimmed } } },
    });
    await query.refetch();
  }

  async function edit(commentId: string, content: string) {
    const trimmed = content.trim();
    if (!trimmed) return;
    await updateMut({
      variables: { id: commentId, input: { data: { content: trimmed } } },
    });
    await query.refetch();
  }

  async function remove(commentId: string) {
    await deleteMut({
      variables: { input: { id: commentId } },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "SharedItemComment", id: commentId }) });
        // Keep the parent SharedItem's comments counter honest so any other
        // view showing it (detail header, list cards) stays in sync without
        // needing SharedItemDetail / SharedItems to refetch.
        cache.modify({
          id: cache.identify({ __typename: "SharedItem", id: itemId }),
          fields: {
            comments: (prev: number) => Math.max(0, (prev ?? 0) - 1),
          },
        });
        cache.gc();
      },
    });
    await query.refetch();
  }

  return {
    comments,
    totalCount,
    loading: query.loading,
    error:
      (query.error as Error | undefined) ??
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
    busy: createState.loading || updateState.loading || deleteState.loading,
    add,
    edit,
    remove,
    refetch: () => query.refetch(),
  };
}
